import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const IMAGE_URLS = [
  '/images/loading-img.png',
  '/images/HeroSection.webp',
];

const ASSET_TIMEOUT_MS = 4500;

interface Props { onReady: () => void; }

const LoadingScreen: React.FC<Props> = ({ onReady }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const totalAssets = IMAGE_URLS.length;
    let resolved = false;
    let ready = 0;

    const markReady = () => {
      if (resolved) return;
      ready += 1;
      setProgress(Math.round((ready / totalAssets) * 100));
      if (ready >= totalAssets) {
        finish();
      }
    };

    const finish = () => {
      if (resolved) return;
      resolved = true;
      window.clearTimeout(cap);
      onReady();
    };

    const cap = window.setTimeout(finish, ASSET_TIMEOUT_MS);

    const images = IMAGE_URLS.map((url) => {
      const img = new Image();
      let done = false;

      const onAssetReady = () => {
        if (done) return;
        done = true;
        markReady();
      };

      img.decoding = 'async';
      img.fetchPriority = url.includes('HeroSection') ? 'high' : 'auto';

      img.onload = () => {
        if (typeof img.decode === 'function') {
          img.decode().catch(() => undefined).finally(onAssetReady);
          return;
        }
        onAssetReady();
      };
      img.onerror = onAssetReady;
      img.src = url;

      if (img.complete && img.naturalWidth > 0) {
        if (typeof img.decode === 'function') {
          img.decode().catch(() => undefined).finally(onAssetReady);
        } else {
          onAssetReady();
        }
      }

      return img;
    });

    return () => {
      resolved = true;
      clearTimeout(cap);
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [onReady]);

  return (
    <motion.div
      data-testid="loading-screen"
      className="fixed inset-0 z-[9999] bg-[#0a0a0a] flex flex-col items-center justify-center"
      exit={{ opacity: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Breathing ambient glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,112,0.07) 0%, transparent 65%)' }}
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Signature — clip reveal (writing effect) */}
      <div className="relative">
        <motion.img
          src="/images/loading-img.png"
          alt="AR.Concept"
          className="w-56 sm:w-72 md:w-80 h-auto relative z-10"
          initial={{ clipPath: 'inset(0 100% 0 0)' }}
          animate={{ clipPath: 'inset(0 0% 0 0)' }}
          transition={{ duration: 1.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
          style={{ filter: 'brightness(1.15)' }}
        />
        <motion.div
          className="absolute inset-0 pointer-events-none -z-10 blur-2xl"
          style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(201,168,112,0.18) 0%, transparent 70%)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.6 }}
        />
      </div>

      {/* Gold line */}
      <motion.div
        className="mt-6 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent"
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: 120, opacity: 1 }}
        transition={{ duration: 0.7, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
      />

      {/* Studio name */}
      <motion.p
        className="mt-5 font-inter text-[9px] tracking-[0.45em] uppercase text-[#F0EDE8]/25"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
      >
        AR.Concept Studio
      </motion.p>

      {/* Progress bar */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 w-32 h-px bg-white/[0.06] overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <motion.div
          className="absolute inset-y-0 left-0 bg-gold/50"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </motion.div>
    </motion.div>
  );
};

export default LoadingScreen;
